import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  Package, Users as UsersIcon, FileText, ClipboardList, AlertTriangle,
  Warehouse, CalendarDays, TrendingUp, TrendingDown, ShieldCheck, CheckCircle2, XCircle, Clock,
  DollarSign, ArrowUpRight, ArrowDownRight, Wallet, Receipt, PackageX, ArrowRight,
  Undo2, FileMinus, Inbox, Settings2, RotateCcw, CheckSquare, Square
} from "lucide-react";

// Definición de widgets configurables del dashboard
const WIDGETS = [
  { key: "stats_basicos", label: "Estadísticos rápidos (Productos, Lotes, Vencidos…)", default: true },
  { key: "kpi_ejecutivo", label: "KPIs ejecutivos (Ventas / CxC / CxP / Stock)", default: true },
  { key: "pendientes_revision", label: "Pendientes de tu revisión", default: true },
  { key: "backorder_disponible", label: "Backorder · pedidos listos para completar", default: true },
  { key: "tesoreria_30d", label: "Flujo de caja · últimos 30 días", default: true },
  { key: "ventas_bajo_minimo", label: "Ventas bajo precio mínimo", default: true },
  { key: "calendario", label: "Calendario · próximos eventos", default: true },
  { key: "tareas", label: "Tareas pendientes", default: true },
  { key: "proximos_pasos", label: "Próximos pasos / guía", default: false },
  { key: "salud_sri", label: "Salud Facturación SRI", default: true },
];

function useDashboardPrefs(userId) {
  const storageKey = `dashboard_widgets_${userId || "anon"}`;
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    const def = {};
    WIDGETS.forEach(w => { def[w.key] = w.default; });
    return def;
  });
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(prefs)); } catch (_) { /* ignore */ }
  }, [prefs, storageKey]);
  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));
  const reset = () => {
    const def = {};
    WIDGETS.forEach(w => { def[w.key] = w.default; });
    setPrefs(def);
  };
  return [prefs, toggle, reset];
}

const ICONOS_PENDIENTES = { Wallet, Undo2, FileMinus, Receipt };

function PendientesRevision({ data }) {
  const total = data?.total_pendientes || 0;
  if (total === 0) {
    return (
      <Card className="p-6 border border-[#d1dbd6]" data-testid="pendientes-revision-empty">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-[#e9f2ee] p-2">
            <CheckCircle2 className="text-[#205c45]" size={20} />
          </div>
          <div>
            <h2 className="text-base font-semibold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Sin pendientes de revisión
            </h2>
            <p className="text-xs text-[#5c7368] mt-0.5">No hay liquidaciones, devoluciones ni NC esperando aprobación.</p>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <Card className="p-6 border border-[#f5c477] bg-gradient-to-br from-[#fef7ec] to-white" data-testid="pendientes-revision-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-[#8a5500] text-white p-2">
            <Inbox size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Pendientes de tu revisión
            </h2>
            <p className="text-[11px] text-[#5c7368] uppercase tracking-wider mt-0.5">
              {total} ítem{total === 1 ? "" : "s"} · Monto total {fmtMoney(data.monto_total)}
            </p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {data.grupos.map((g) => {
          const Icon = ICONOS_PENDIENTES[g.icono] || Inbox;
          const empty = g.count === 0;
          return (
            <Link
              key={g.clave}
              to={g.ruta}
              className={`rounded-md border p-4 transition group ${empty ? "border-[#d1dbd6] bg-white opacity-60" : "border-[#f5c477] bg-white hover:shadow-md hover:border-[#8a5500]"}`}
              data-testid={`pendiente-grupo-${g.clave}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Icon size={18} className={empty ? "text-[#8ba397]" : "text-[#8a5500]"} />
                  <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#5c7368] leading-tight">
                    {g.label}
                  </div>
                </div>
                <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${empty ? "bg-[#f0f4f2] text-[#8ba397]" : "bg-[#8a5500] text-white"}`} data-testid={`pendiente-count-${g.clave}`}>
                  {g.count}
                </span>
              </div>
              <div className="text-2xl font-bold text-[#15241d] tabular-nums" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {fmtMoney(g.monto_total)}
              </div>
              <div className="text-[10px] text-[#5c7368] uppercase tracking-wider mt-0.5">{g.rol_aprobador}</div>
              {g.items?.length > 0 && (
                <ul className="mt-3 space-y-1 border-t border-[#e6ebe8] pt-2">
                  {g.items.slice(0, 3).map((it) => (
                    <li key={it.id} className="text-[11px] flex justify-between gap-2 leading-tight">
                      <span className="text-[#15241d] truncate flex-1" title={`${it.titulo} · ${it.subtitulo || ""}`}>
                        {it.titulo}
                      </span>
                      <span className="tabular-nums text-[#205c45] font-semibold whitespace-nowrap">{fmtMoney(it.monto)}</span>
                    </li>
                  ))}
                  {g.count > 3 && (
                    <li className="text-[10px] text-[#5c7368] italic pt-0.5">+{g.count - 3} más…</li>
                  )}
                </ul>
              )}
              {!empty && (
                <div className="mt-2 text-[11px] text-[#8a5500] font-semibold flex items-center gap-1 group-hover:underline">
                  Revisar <ArrowRight size={11} />
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

function StatCard({ icon: Icon, label, value, tone = "default", testId }) {
  const tones = {
    default: "bg-white border-[#d1dbd6]",
    warning: "bg-[#fef7ec] border-[#f5c477]",
    danger: "bg-[#fdecea] border-[#d99584]",
    primary: "bg-[#e9f2ee] border-[#b2d0c2]",
  };
  return (
    <div className={`rounded-md border p-5 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">{label}</div>
        <Icon size={18} className="text-[#205c45]" strokeWidth={1.8} />
      </div>
      <div className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

const fmtMoney = (v) => `$${(v || 0).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MiniSpark({ data }) {
  if (!data || data.length < 2) return null;
  const w = 280, h = 48;
  const vals = data.map(d => d.total);
  const max = Math.max(...vals, 1);
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (d.total / max) * (h - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline fill="none" stroke="#205c45" strokeWidth="1.8" points={pts} />
      <polyline fill="rgba(32,92,69,0.08)" stroke="none" points={`0,${h} ${pts} ${w},${h}`} />
    </svg>
  );
}

function KPIExecutive({ kpis }) {
  const v = kpis.ventas_mes || {};
  const cxc = kpis.cxc || {};
  const cxp = kpis.cxp || {};
  const stock = kpis.stock || {};
  const ret = kpis.retenciones || {};
  const tes = kpis.tesoreria || {};
  const VarIcon = (v.variacion_pct || 0) >= 0 ? ArrowUpRight : ArrowDownRight;
  const varColor = (v.variacion_pct || 0) >= 0 ? "text-[#205c45]" : "text-[#7a2514]";

  return (
    <div className="space-y-4" data-testid="kpi-executive">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Panel ejecutivo
        </h2>
        <span className="text-xs text-[#5c7368]">Mes en curso</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Ventas del mes */}
        <Link to="/facturacion" className="group rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-5 hover:shadow-md transition" data-testid="kpi-ventas">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#205c45]">Ventas del mes</div>
            <DollarSign size={18} className="text-[#205c45]" />
          </div>
          <div className="text-3xl font-bold text-[#15241d] tabular-nums" style={{ fontFamily: "'Manrope', sans-serif" }}>{fmtMoney(v.total)}</div>
          <div className="flex items-center gap-2 mt-2 text-xs">
            <span className={`flex items-center gap-1 font-semibold ${varColor}`}>
              <VarIcon size={14} /> {Math.abs(v.variacion_pct || 0)}%
            </span>
            <span className="text-[#5c7368]">vs {fmtMoney(v.mes_pasado)} mes pasado</span>
          </div>
          <div className="mt-3">
            <MiniSpark data={v.serie_30_dias} />
          </div>
          <div className="text-[11px] text-[#5c7368] mt-1">{v.n_facturas} facturas · Autoriz. {fmtMoney(v.autorizadas)}</div>
        </Link>

        {/* CxC */}
        <Link to="/contabilidad" className="group rounded-md border border-[#d1dbd6] bg-white p-5 hover:shadow-md transition" data-testid="kpi-cxc">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">Cuentas por cobrar</div>
            <ArrowDownRight size={18} className="text-[#205c45]" />
          </div>
          <div className="text-3xl font-bold text-[#15241d] tabular-nums" style={{ fontFamily: "'Manrope', sans-serif" }}>{fmtMoney(cxc.total_abierto)}</div>
          <div className="text-xs text-[#5c7368] mt-1">{cxc.n} documentos abiertos</div>
          {cxc.vencido > 0 && (
            <div className="mt-3 rounded bg-[#fdecea] border border-[#d99584] px-2 py-1.5 text-[11px] text-[#7a2514]">
              <strong>{fmtMoney(cxc.vencido)}</strong> vencidos ({cxc.n_vencidas})
            </div>
          )}
          <div className="mt-2 text-xs text-[#205c45] font-medium group-hover:underline flex items-center gap-1">Gestionar cobros <ArrowRight size={12} /></div>
        </Link>

        {/* CxP */}
        <Link to="/contabilidad" className="group rounded-md border border-[#d1dbd6] bg-white p-5 hover:shadow-md transition" data-testid="kpi-cxp">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">Cuentas por pagar</div>
            <ArrowUpRight size={18} className="text-[#7a2514]" />
          </div>
          <div className="text-3xl font-bold text-[#15241d] tabular-nums" style={{ fontFamily: "'Manrope', sans-serif" }}>{fmtMoney(cxp.total_abierto)}</div>
          <div className="text-xs text-[#5c7368] mt-1">{cxp.n} documentos abiertos</div>
          {cxp.vencido > 0 && (
            <div className="mt-3 rounded bg-[#fdecea] border border-[#d99584] px-2 py-1.5 text-[11px] text-[#7a2514]">
              <strong>{fmtMoney(cxp.vencido)}</strong> vencidos ({cxp.n_vencidas})
            </div>
          )}
          <div className="mt-2 text-xs text-[#205c45] font-medium group-hover:underline flex items-center gap-1">Programar pagos <ArrowRight size={12} /></div>
        </Link>

        {/* Stock en riesgo */}
        <Link to="/inventario-reporte" className="group rounded-md border border-[#f5c477] bg-[#fef7ec] p-5 hover:shadow-md transition" data-testid="kpi-stock">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#8a5500]">Stock en riesgo</div>
            <PackageX size={18} className="text-[#8a5500]" />
          </div>
          <div className="text-3xl font-bold text-[#15241d] tabular-nums" style={{ fontFamily: "'Manrope', sans-serif" }}>{fmtMoney(stock.valor_90dias)}</div>
          <div className="text-xs text-[#5c7368] mt-1">{stock.n_lotes_90dias} lotes vencen en 90 días</div>
          {stock.n_lotes_vencidos > 0 && (
            <div className="mt-3 rounded bg-[#fdecea] border border-[#d99584] px-2 py-1.5 text-[11px] text-[#7a2514]">
              <strong>{fmtMoney(stock.valor_vencido)}</strong> ya vencido ({stock.n_lotes_vencidos})
            </div>
          )}
          <div className="mt-2 text-xs text-[#205c45] font-medium group-hover:underline flex items-center gap-1">Ver inventario <ArrowRight size={12} /></div>
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Retenciones */}
        <div className="rounded-md border border-[#d1dbd6] bg-white p-5" data-testid="kpi-retenciones">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">Retenciones del periodo</div>
            <Receipt size={18} className="text-[#205c45]" />
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Emitidas a proveedores</div>
              <div className="text-xl font-bold text-[#15241d] tabular-nums mt-0.5">{fmtMoney(ret.emitidas)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#5c7368]">Recibidas de clientes</div>
              <div className="text-xl font-bold text-[#205c45] tabular-nums mt-0.5">{fmtMoney(ret.recibidas)}</div>
            </div>
          </div>
        </div>

        {/* Tesorería */}
        <div className="rounded-md border border-[#d1dbd6] bg-white p-5" data-testid="kpi-tesoreria">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">Tesorería del mes</div>
            <Wallet size={18} className="text-[#205c45]" />
          </div>
          <div className="text-xs text-[#5c7368] flex justify-between mt-2"><span>Cobrado</span><span className="tabular-nums font-semibold text-[#205c45]">{fmtMoney(tes.cobrado_mes)}</span></div>
          <div className="text-xs text-[#5c7368] flex justify-between mt-1"><span>Pagado</span><span className="tabular-nums font-semibold text-[#7a2514]">{fmtMoney(tes.pagado_mes)}</span></div>
          <div className="border-t border-[#d1dbd6] mt-2 pt-2 flex justify-between items-center">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-[#5c7368]">Flujo neto</span>
            <span className={`tabular-nums font-bold text-lg ${(tes.flujo_neto || 0) >= 0 ? "text-[#205c45]" : "text-[#7a2514]"}`}>{fmtMoney(tes.flujo_neto)}</span>
          </div>
        </div>

        {/* Top clientes */}
        <div className="rounded-md border border-[#d1dbd6] bg-white p-5" data-testid="kpi-top-clientes">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">Top 5 clientes del mes</div>
            <UsersIcon size={18} className="text-[#205c45]" />
          </div>
          {v.top_clientes?.length ? (
            <ol className="space-y-1.5 text-xs">
              {v.top_clientes.map((c, i) => (
                <li key={c.cliente_id} className="flex justify-between items-baseline gap-2">
                  <span className="text-[#15241d] truncate"><span className="text-[#8ba397] font-mono mr-1">{i + 1}.</span>{c.razon_social}</span>
                  <span className="tabular-nums font-semibold text-[#205c45] whitespace-nowrap">{fmtMoney(c.total)}</span>
                </li>
              ))}
            </ol>
          ) : <div className="text-xs text-[#8ba397] italic">Sin ventas aún este mes</div>}
        </div>
      </div>
    </div>
  );
}

/* ============ ITER168: Calendario + Tareas ============ */
const TIPO_COLORS = {
  cita: { bg: "bg-[#e9f2ee]", text: "text-[#205c45]", border: "border-[#b2d0c2]" },
  recordatorio: { bg: "bg-[#fef7ec]", text: "text-[#8a5500]", border: "border-[#f5c477]" },
  tarea: { bg: "bg-[#eef2f7]", text: "text-[#28517a]", border: "border-[#a9c1d9]" },
  alerta: { bg: "bg-[#fdecea]", text: "text-[#7a2514]", border: "border-[#d99584]" },
  nota: { bg: "bg-[#f4f0ea]", text: "text-[#5c4a2a]", border: "border-[#d9c4a0]" },
};
const _pad = (n) => String(n).padStart(2, "0");
const _toDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
};
const _fmtFechaCorta = (iso) => {
  const d = _toDate(iso);
  if (!d) return "—";
  const meses = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const isDateTime = iso && iso.length > 10;
  const base = `${_pad(d.getDate())} ${meses[d.getMonth()]}`;
  return isDateTime ? `${base} · ${_pad(d.getHours())}:${_pad(d.getMinutes())}` : base;
};

function CalendarioWidget() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/calendar/eventos").then(r => setItems(r.data)).catch(() => setItems([]));
  }, []);
  const upcoming = useMemo(() => {
    if (!items) return null;
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    // ITER208: incluir eventos EN CURSO (empezaron antes pero terminan hoy o
    // después) además de los futuros. Antes desaparecían del widget.
    return items
      .filter(ev => {
        if (ev.completado) return false;
        const ini = _toDate(ev.fecha_inicio);
        const fin = _toDate(ev.fecha_fin) || ini;
        if (!ini) return false;
        return (fin && fin >= hoy) || ini >= hoy;
      })
      .sort((a, b) => (_toDate(a.fecha_inicio)?.getTime() || 0) - (_toDate(b.fecha_inicio)?.getTime() || 0))
      .slice(0, 30);
  }, [items]);
  return (
    <Card className="p-6 border border-[#d1dbd6]" data-testid="dashboard-calendario-widget">
      <div className="flex items-center gap-3 mb-4">
        <CalendarDays className="text-[#205c45]" size={20} />
        <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Calendario · próximos eventos
        </h2>
        <Link to="/calendario" className="ml-auto text-xs text-[#205c45] hover:underline flex items-center gap-1" data-testid="dashboard-calendario-link">
          Ver todo <ArrowRight size={12} />
        </Link>
      </div>
      {upcoming === null ? (
        <div className="text-xs text-[#8ba397] italic">Cargando…</div>
      ) : upcoming.length === 0 ? (
        <div className="text-sm text-[#8ba397] italic py-6 text-center">
          No hay eventos próximos.<br />
          <Link to="/calendario" className="text-[#205c45] hover:underline">Crear un evento</Link>
        </div>
      ) : (
        <ul className="space-y-2 max-h-[320px] overflow-y-auto pr-1" data-testid="dashboard-calendario-scroll">
          {upcoming.map(ev => {
            const c = TIPO_COLORS[ev.tipo] || TIPO_COLORS.nota;
            return (
              <li key={ev.id} className={`rounded-md border ${c.border} ${c.bg} p-3 flex gap-3 items-start`} data-testid={`dashboard-cal-item-${ev.id}`}>
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${c.text} min-w-[70px]`}>
                  <div>{_fmtFechaCorta(ev.fecha_inicio)}</div>
                  <div className="mt-0.5 opacity-70">{ev.tipo}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#15241d] truncate">{ev.titulo}</div>
                  {ev.descripcion && <div className="text-xs text-[#5c7368] truncate mt-0.5">{ev.descripcion}</div>}
                  {ev.fecha_fin && (
                    <div className="text-[10px] text-[#5c7368] mt-0.5">Hasta {_fmtFechaCorta(ev.fecha_fin)}</div>
                  )}
                  {ev.prioridad === "alta" && <span className="inline-block mt-1 text-[10px] uppercase font-semibold text-[#7a2514] bg-[#fdecea] border border-[#d99584] rounded px-1.5 py-0.5">Alta prioridad</span>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function TareasWidget() {
  const [items, setItems] = useState(null);
  // ITER208: el widget lee el módulo REAL de tareas (/tareas), no el calendario.
  const load = () => Promise.all([
    api.get("/tareas?asignado_a=me"),
    api.get("/tareas?creadas_por_mi=true"),
  ]).then(([a, b]) => {
    const map = new Map();
    [...(a.data || []), ...(b.data || [])].forEach(t => map.set(t.id, t));
    setItems([...map.values()]);
  }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const pendientes = useMemo(() => {
    if (!items) return null;
    const prio = { urgente: 0, alta: 1, media: 2, baja: 3 };
    return items
      .filter(t => ["pendiente", "en_progreso"].includes(t.estado))
      .sort((a, b) => {
        const p = (prio[a.prioridad] ?? 2) - (prio[b.prioridad] ?? 2);
        if (p !== 0) return p;
        return (a.fecha_limite || "9999").localeCompare(b.fecha_limite || "9999");
      })
      .slice(0, 40);
  }, [items]);

  const completar = async (t) => {
    try {
      await api.patch(`/tareas/${t.id}`, { estado: "completada" });
      toast.success("Tarea completada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const hoyISO = new Date().toISOString().slice(0, 10);

  return (
    <Card className="p-6 border border-[#d1dbd6]" data-testid="dashboard-tareas-widget">
      <div className="flex items-center gap-3 mb-4">
        <CheckSquare className="text-[#205c45]" size={20} />
        <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Tareas pendientes
        </h2>
        {pendientes && pendientes.length > 0 && (
          <span className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] bg-[#e9f2ee] border border-[#b2d0c2] rounded-full px-2 py-0.5">
            {pendientes.length}
          </span>
        )}
        <Link to="/tareas" className="ml-auto text-xs text-[#205c45] hover:underline flex items-center gap-1" data-testid="dashboard-tareas-link">
          Ver todo <ArrowRight size={12} />
        </Link>
      </div>
      {pendientes === null ? (
        <div className="text-xs text-[#8ba397] italic">Cargando…</div>
      ) : pendientes.length === 0 ? (
        <div className="text-sm text-[#8ba397] italic py-6 text-center">
          Nada pendiente por hacer.<br />
          <Link to="/tareas" className="text-[#205c45] hover:underline">Crear una tarea</Link>
        </div>
      ) : (
        <ul className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1" data-testid="dashboard-tareas-scroll">
          {pendientes.map(t => {
            const vencida = t.fecha_limite && t.fecha_limite < hoyISO;
            const colorPrio = t.prioridad === "urgente" ? "bg-[#b53a25]"
              : t.prioridad === "alta" ? "bg-[#c47a1a]"
              : t.prioridad === "baja" ? "bg-[#8ba397]" : "bg-[#205c45]";
            return (
              <li key={t.id} className="flex items-start gap-2 p-2 rounded-md hover:bg-[#f7f9f8] transition-colors" data-testid={`dashboard-tarea-${t.id}`}>
                <button onClick={() => completar(t)} className="mt-0.5 shrink-0" title="Marcar como completada" data-testid={`dashboard-tarea-toggle-${t.id}`}>
                  <Square size={16} className="text-[#8ba397] hover:text-[#205c45]" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#15241d] font-medium truncate flex items-center gap-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${colorPrio}`}></span>
                    {t.titulo}
                    {["alta", "urgente"].includes(t.prioridad) && (
                      <span className="text-[10px] uppercase font-semibold text-[#7a2514]">·{t.prioridad}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#5c7368] flex items-center gap-2 mt-0.5 flex-wrap">
                    {t.fecha_limite ? <span>Límite {_fmtFechaCorta(t.fecha_limite)}</span> : <span className="opacity-70">Sin fecha límite</span>}
                    {vencida && <span className="text-[#7a2514] font-semibold">VENCIDA</span>}
                    {t.estado === "en_progreso" && <span className="text-[#205c45] font-semibold">EN PROGRESO</span>}
                    {t.asignado_a_nombre && <span className="opacity-70">· {t.asignado_a_nombre}</span>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}


function BackorderWidget() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/sales/backorder");
        if (alive) setItems(data);
      } catch (_) { if (alive) setItems([]); }
    })();
    return () => { alive = false; };
  }, []);
  if (items === null) {
    return (
      <Card className="p-4 border border-[#d1dbd6]" data-testid="dashboard-backorder-loading">
        <div className="text-xs text-[#8ba397] italic">Cargando backorder…</div>
      </Card>
    );
  }
  const listos = items.filter(x => x.hay_stock_para_np_complementaria);
  const pendientes = items.filter(x => !x.hay_stock_para_np_complementaria);
  if (items.length === 0) return null;
  return (
    <Card
      className={`p-6 border ${listos.length > 0 ? "border-[#4a8f5f] bg-gradient-to-br from-[#e9f2ee] to-white" : "border-[#d1dbd6]"}`}
      data-testid="dashboard-backorder-widget"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`rounded-full p-2 ${listos.length > 0 ? "bg-[#4a8f5f] text-white animate-pulse" : "bg-[#e9f2ee] text-[#5c7368]"}`}>
          <Package size={20} />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {listos.length > 0 ? "¡Hay pedidos listos para completar!" : "Backorder en espera"}
          </h2>
          <p className="text-xs text-[#5c7368] mt-0.5">
            {listos.length > 0
              ? `${listos.length} pedido(s) con mercadería lista para despachar.`
              : `${pendientes.length} pedido(s) esperan que llegue mercadería.`}
          </p>
        </div>
        <Link
          to="/ventas/backorder"
          className="text-xs bg-[#173d34] hover:bg-[#0f2924] text-white px-3 py-1.5 rounded-md flex items-center gap-1"
          data-testid="dashboard-backorder-link"
        >
          Revisar <ArrowRight size={12} />
        </Link>
      </div>
      {listos.slice(0, 3).length > 0 && (
        <ul className="space-y-1.5 mt-2">
          {listos.slice(0, 3).map(s => (
            <li key={s.id} className="flex items-center justify-between text-sm border border-[#d1dbd6] bg-white rounded px-2 py-1.5">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{s.numero} · {s.cliente_nombre || "—"}</div>
                <div className="text-xs text-[#5c7368]">
                  {s.total_lineas_pendientes} línea(s) pendiente(s) · {s.total_disponible_ahora} listo(s) para despachar
                </div>
              </div>
              <CheckCircle2 className="text-[#4a8f5f] flex-shrink-0" size={16} />
            </li>
          ))}
          {listos.length > 3 && (
            <li className="text-xs text-[#5c7368] text-center pt-1">... y {listos.length - 3} pedido(s) más</li>
          )}
        </ul>
      )}
    </Card>
  );
}


export default function Dashboard() {
  const { user, hasModule } = useAuth();
  const [stats, setStats] = useState({});
  const [sri, setSri] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [bajoMin, setBajoMin] = useState(null);
  const [tesoreria, setTesoreria] = useState(null);
  const [pendientesRev, setPendientesRev] = useState(null);
  const [prefs, togglePref, resetPrefs] = useDashboardPrefs(user?.id);

  useEffect(() => {
    const load = async () => {
      try {
        const s = {};
        if (hasModule("bodega")) {
          const { data } = await api.get("/warehouse/resumen");
          Object.assign(s, data);
        }
        if (hasModule("ventas")) {
          const [{ data: cot }, { data: ped }] = await Promise.all([
            api.get("/sales/cotizaciones"),
            api.get("/sales/pedidos"),
          ]);
          s.cotizaciones = cot.length;
          s.pedidos = ped.length;
        }
        if (hasModule("maestros")) {
          const [{ data: cli }] = await Promise.all([api.get("/masters/clientes")]);
          s.clientes = cli.length;
        }
        setStats(s);
        if (hasModule("gerencia")) {
          try {
            const { data: h } = await api.get("/gerencia/salud-sri");
            setSri(h);
          } catch (_) { /* módulo no disponible */ }
          try {
            const { data: k } = await api.get("/dashboard/kpis");
            setKpis(k);
          } catch (_) { /* no data yet */ }
          try {
            const { data: pr } = await api.get("/dashboard/pendientes-revision");
            setPendientesRev(pr);
          } catch (_) { /* sin permiso o sin pendientes */ }
          try {
            const { data: b } = await api.get("/gastos/kpis/ventas-bajo-minimo-mes");
            setBajoMin(b);
          } catch (_) { /* no data yet */ }
          try {
            const { data: t } = await api.get("/gastos/tesoreria/flujo?dias=30");
            setTesoreria(t);
          } catch (_) { /* no data yet */ }
        }
      } catch (e) {
        console.error(e);
      }
    };
    load();
  }, [hasModule]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Mi espacio</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Bienvenido, {user?.nombres}
          </h1>
          <p className="text-sm text-[#4a5c53] mt-2 max-w-2xl">
            Resumen operativo de su distribuidora. Los indicadores que ve dependen de los módulos
            habilitados para su usuario y de las tarjetas que tenga activadas.
          </p>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="flex-shrink-0" data-testid="dashboard-personalizar-btn">
              <Settings2 size={14} className="mr-2" /> Personalizar
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[320px] p-0">
            <div className="px-4 py-3 border-b border-[#d1dbd6] bg-[#f7f9f8] flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Tarjetas visibles</div>
              <button
                type="button"
                onClick={resetPrefs}
                className="text-[11px] text-[#205c45] hover:underline flex items-center gap-1"
                data-testid="dashboard-reset-prefs"
              >
                <RotateCcw size={11} /> Predeterminados
              </button>
            </div>
            <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
              {WIDGETS.map(w => (
                <label key={w.key} className="flex items-start gap-3 p-2 rounded hover:bg-[#f7f9f8] cursor-pointer">
                  <Switch
                    checked={!!prefs[w.key]}
                    onCheckedChange={() => togglePref(w.key)}
                    data-testid={`dashboard-toggle-${w.key}`}
                  />
                  <span className="text-xs text-[#15241d] leading-tight">{w.label}</span>
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {prefs.stats_basicos && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {hasModule("bodega") && (
          <>
            <StatCard icon={Package} label="Productos" value={stats.total_productos ?? "—"} testId="stat-productos" />
            <StatCard icon={Warehouse} label="Lotes activos" value={stats.total_lotes_activos ?? "—"} testId="stat-lotes" />
            <StatCard icon={AlertTriangle} label="Vencidos" value={stats.vencidos ?? 0} tone="danger" testId="stat-vencidos" />
            <StatCard icon={AlertTriangle} label="Críticos (≤30d)" value={stats.criticos_30d ?? 0} tone="warning" testId="stat-criticos" />
          </>
        )}
        {hasModule("ventas") && (
          <>
            <StatCard icon={FileText} label="Cotizaciones" value={stats.cotizaciones ?? "—"} tone="primary" testId="stat-cotizaciones" />
            <StatCard icon={ClipboardList} label="Notas de pedido" value={stats.pedidos ?? "—"} tone="primary" testId="stat-pedidos" />
          </>
        )}
        {hasModule("maestros") && (
          <StatCard icon={UsersIcon} label="Clientes" value={stats.clientes ?? "—"} testId="stat-clientes" />
        )}
      </div>
      )}

      {prefs.kpi_ejecutivo && kpis && hasModule("gerencia") && (
        <KPIExecutive kpis={kpis} />
      )}

      {prefs.pendientes_revision && pendientesRev && hasModule("gerencia") && (
        <PendientesRevision data={pendientesRev} />
      )}

      {prefs.backorder_disponible && hasModule("ventas") && (
        <BackorderWidget />
      )}

      {prefs.tesoreria_30d && tesoreria && hasModule("gerencia") && (
        <Card className="p-6 border border-[#d1dbd6]" data-testid="tesoreria-card">
          <div className="flex items-center gap-3 mb-1">
            <Wallet className="text-[#205c45]" size={20} />
            <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Flujo de caja · últimos 30 días
            </h2>
            <span className={`ml-auto text-xs uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 ${tesoreria.neto >= 0 ? "bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]" : "bg-[#fdecea] text-[#7a2514] border border-[#d99584]"}`}>
              Neto: {fmtMoney(tesoreria.neto)}
            </span>
          </div>
          <p className="text-xs text-[#5c7368] mb-4">Cobros de clientes (CxC) vs pagos a proveedores (CxP) y gastos operativos.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#205c45]">Ingresos (Cobros)</div>
              <div className="text-2xl font-bold tabular-nums text-[#15241d] mt-1">{fmtMoney(tesoreria.total_ingresos)}</div>
              <div className="text-[10px] text-[#4a5c53]">Promedio diario {fmtMoney(tesoreria.promedio_diario_ingresos)}</div>
            </div>
            <div className="rounded-md border border-[#d99584] bg-[#fdecea] p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#7a2514]">Egresos totales</div>
              <div className="text-2xl font-bold tabular-nums text-[#15241d] mt-1">{fmtMoney(tesoreria.total_egresos)}</div>
              <div className="text-[10px] text-[#4a5c53]">Pagos CxP {fmtMoney(tesoreria.desglose_egresos?.pagos_cxp)} · Gastos {fmtMoney(tesoreria.desglose_egresos?.gastos_operativos)}</div>
            </div>
            <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Días positivos</div>
              <div className="text-2xl font-bold tabular-nums text-[#205c45] mt-1">{tesoreria.n_dias_positivos}</div>
              <div className="text-[10px] text-[#4a5c53]">de {tesoreria.serie?.length || 0}</div>
            </div>
            <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Días negativos</div>
              <div className="text-2xl font-bold tabular-nums text-[#7a2514] mt-1">{tesoreria.n_dias_negativos}</div>
              <div className="text-[10px] text-[#4a5c53]">de {tesoreria.serie?.length || 0}</div>
            </div>
          </div>
          {/* Mini chart de barras (ingresos verde, egresos rojo) */}
          {tesoreria.serie?.length > 0 && (() => {
            const w = 800, h = 110, pad = 8;
            const maxV = Math.max(1, ...tesoreria.serie.map(r => Math.max(r.ingresos, r.egresos)));
            const bw = (w - pad * 2) / tesoreria.serie.length;
            return (
              <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
                {tesoreria.serie.map((r, i) => {
                  const x = pad + i * bw;
                  const ingH = (r.ingresos / maxV) * (h / 2 - 4);
                  const egH = (r.egresos / maxV) * (h / 2 - 4);
                  return (
                    <g key={i}>
                      <rect x={x + 1} y={h / 2 - ingH} width={Math.max(1, bw - 2)} height={ingH} fill="#205c45" opacity="0.85">
                        <title>{r.fecha}: ingresos {fmtMoney(r.ingresos)}</title>
                      </rect>
                      <rect x={x + 1} y={h / 2} width={Math.max(1, bw - 2)} height={egH} fill="#b53a25" opacity="0.85">
                        <title>{r.fecha}: egresos {fmtMoney(r.egresos)}</title>
                      </rect>
                    </g>
                  );
                })}
                <line x1="0" y1={h / 2} x2={w} y2={h / 2} stroke="#5c7368" strokeWidth="0.4" />
              </svg>
            );
          })()}
          <div className="mt-2 flex items-center gap-4 text-[10px] text-[#5c7368]">
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#205c45]"></span>Ingresos diarios</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#b53a25]"></span>Egresos diarios</span>
            <span className="ml-auto">{tesoreria.desde} → {tesoreria.hasta}</span>
          </div>
        </Card>
      )}

      {prefs.ventas_bajo_minimo && bajoMin && hasModule("gerencia") && bajoMin.n_facturas_bajo_minimo > 0 && (
        <Card className="p-6 border border-[#d99584] bg-gradient-to-r from-[#fdecea] to-[#fef7ec]" data-testid="kpi-bajo-minimo">
          <div className="flex items-start gap-4">
            <div className="rounded-full bg-[#b53a25] text-white p-2 mt-1">
              <AlertTriangle size={20} />
            </div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-base font-bold text-[#7a2514]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Ventas autorizadas bajo precio mínimo · {bajoMin.periodo}
                </h2>
                <Link to="/facturacion" className="text-xs uppercase tracking-wider font-semibold text-[#7a2514] hover:underline flex items-center gap-1">
                  Ver facturas <ArrowRight size={12} />
                </Link>
              </div>
              <div className="grid md:grid-cols-3 gap-4 mt-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">N° Facturas</div>
                  <div className="text-2xl font-bold tabular-nums text-[#7a2514]">{bajoMin.n_facturas_bajo_minimo}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Monto facturado</div>
                  <div className="text-2xl font-bold tabular-nums text-[#15241d]">{fmtMoney(bajoMin.monto_total_bajo_minimo)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Diferencia acumulada (vs P.Min)</div>
                  <div className="text-2xl font-bold tabular-nums text-[#7a2514]">{fmtMoney(bajoMin.diferencia_acumulada)}</div>
                </div>
              </div>
              {bajoMin.ultimas?.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-[#7a2514] font-semibold uppercase tracking-wider">Últimas {Math.min(bajoMin.ultimas.length, 10)} facturas</summary>
                  <ul className="mt-2 space-y-1">
                    {bajoMin.ultimas.map((f) => (
                      <li key={f.id} className="flex justify-between gap-2 border-b border-[#e6ebe8] py-1">
                        <span className="font-mono text-[#15241d]">{f.numero}</span>
                        <span className="truncate flex-1">{f.cliente_nombre}</span>
                        <span className="tabular-nums text-[#7a2514] font-semibold">{fmtMoney(f.total)}</span>
                        {f.autorizado_por_pin && <span className="text-[10px] italic text-[#5c7368]">aut. {f.autorizado_por_pin.nombre || f.autorizado_por_pin.email}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>
        </Card>
      )}

      {prefs.proximos_pasos && (
      <Card className="p-6 border border-[#d1dbd6]">
        <div className="flex items-center gap-3 mb-4">
          <TrendingUp className="text-[#205c45]" size={20} />
          <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Próximos pasos
          </h2>
        </div>
        <ul className="text-sm text-[#4a5c53] space-y-2 list-disc pl-6">
          {user?.role === "admin" && <li>Cree usuarios y asígneles los módulos adecuados desde <strong>Seguridad → Usuarios</strong>.</li>}
          {hasModule("maestros") && <li>Complete sus catálogos (Laboratorios, Marcas, Presentaciones) antes de crear productos.</li>}
          {hasModule("maestros") && <li>Registre productos en los tres niveles: Base → Comercial → Lotes.</li>}
          {hasModule("ventas") && <li>Los vendedores solo ven sus clientes, cotizaciones y pedidos propios.</li>}
          {hasModule("bodega") && <li>El sistema sugiere lote por FEFO (el que vence primero).</li>}
        </ul>
      </Card>
      )}

      {prefs.calendario && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CalendarioWidget />
          {prefs.tareas && <TareasWidget />}
        </div>
      )}
      {prefs.tareas && !prefs.calendario && <TareasWidget />}

      {prefs.salud_sri && sri && (
        <Card className="p-6 border border-[#d1dbd6]" data-testid="sri-health-card">
          <div className="flex items-center gap-3 mb-1">
            <ShieldCheck className="text-[#205c45]" size={20} />
            <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Salud Facturación SRI
            </h2>
            <span className="ml-auto text-xs uppercase tracking-wider font-semibold text-[#5c7368] bg-[#e9f2ee] border border-[#b2d0c2] rounded-full px-2 py-0.5">
              {sri.ambiente === "2" ? "Producción" : "Pruebas"} · últimos {sri.periodo_dias}d
            </span>
          </div>
          <p className="text-xs text-[#5c7368] mb-4">Estado de facturación electrónica ante el SRI.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-4">
              <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-wider font-semibold text-[#205c45]">% Éxito</div><CheckCircle2 size={16} className="text-[#205c45]" /></div>
              <div className="text-2xl font-bold text-[#15241d] mt-1 tabular-nums">{sri.pct_exito}%</div>
              <div className="text-[11px] text-[#4a5c53] mt-1">{sri.autorizadas} de {sri.total_facturas} autorizadas</div>
            </div>
            <div className={`rounded-md border p-4 ${sri.rechazadas > 0 ? "border-[#d99584] bg-[#fdecea]" : "border-[#d1dbd6] bg-white"}`}>
              <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Rechazadas</div><XCircle size={16} className={sri.rechazadas > 0 ? "text-[#7a2514]" : "text-[#8ba397]"} /></div>
              <div className="text-2xl font-bold text-[#15241d] mt-1 tabular-nums">{sri.rechazadas}</div>
              <div className="text-[11px] text-[#4a5c53] mt-1">Requieren corrección</div>
            </div>
            <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
              <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Tiempo medio</div><Clock size={16} className="text-[#5c7368]" /></div>
              <div className="text-2xl font-bold text-[#15241d] mt-1 tabular-nums">{sri.tiempo_medio_autorizacion_seg}s</div>
              <div className="text-[11px] text-[#4a5c53] mt-1">Firma → autorización</div>
            </div>
            <div className="rounded-md border border-[#d1dbd6] bg-white p-4">
              <div className="flex items-center justify-between"><div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Monto autorizado</div><FileText size={16} className="text-[#5c7368]" /></div>
              <div className="text-2xl font-bold text-[#15241d] mt-1 tabular-nums">${(sri.monto_autorizado || 0).toLocaleString("es-EC", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
              <div className="text-[11px] text-[#4a5c53] mt-1">{sri.firmadas_pendientes + sri.enviadas_sin_auth} pendientes</div>
            </div>
          </div>
          {sri.ultimas_rechazadas?.length > 0 && (
            <div className="mt-4 text-xs text-[#7a2514]">
              <strong>Últimas rechazadas:</strong> {sri.ultimas_rechazadas.map(r => r.numero).join(", ")}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
